// EXEMPT
// APP WIDE GLOBAL CONSTANTS
import { appURLs } from "@/app/app-urls.mjs";
import type { GamesWhereProp, GameSymbol } from "@/app/games.mjs";
import gamesList, { deriveGamesPropMap } from "@/app/games.mjs";
import { GAME_SYMBOL_APEX } from "@/game-apex/definition-symbol.mjs";
import { GAME_SYMBOL_CS2 } from "@/game-csgo/definition-symbol.mjs";
import { GAME_SYMBOL_DEADLOCK } from "@/game-deadlock/definition-symbol.mjs";
import { GAME_SYMBOL_EFT } from "@/game-eft/definition-symbol.mjs";
import { GAME_SYMBOL_FORTNITE } from "@/game-fortnite/definition-symbol.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import { GAME_SYMBOL_MARVEL_RIVALS } from "@/game-marvel-rivals/definition-symbol.mjs";
import { GAME_SYMBOL_MINECRAFT } from "@/game-minecraft/definition.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import { GAME_SYMBOL_UNKNOWN } from "@/game-unknown/definition-symbol.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import makeStrictKeysObject from "@/util/strict-keys-object.mjs";
import SymbolMap from "@/util/symbol-map.mjs";
import symbolName from "@/util/symbol-name.mjs";

// TODO: refactor by removing this export, prefer `app/app-urls`
export { appURLs } from "@/app/app-urls.mjs";

export const MIN_STRING_DISTANCE = 0.8;

export const CLUTCH_HEAD_IMG =
  "https://blitz-cdn.blitz.gg/blitz/ui/img/misc/clutch-head.webp";
// This refers to the file extension used by esbuild output. By default,
// it is .js, but .mjs is preferred.
export const JS_FILE_EXTENSION = __JS_FILE_EXTENSION__ ?? ".js";

// This is mainly for external / third-party URLs!
export const hardCodeURLs = makeStrictKeysObject({
  RITO_THIRD_PARTY:
    "https://support.riotgames.com/hc/en-us/articles/225266848-Third-Party-Applications",
  DEFAULT_OG_IMAGE: "https://blitz-cdn-plain.blitz.gg/blitz/og_image.png",
  BLITZ_SUPPORT: "mailto:support@blitz.gg",
  BLITZ_FACEBOOK: "https://www.facebook.com/theblitzapp/",
  BLITZ_TWITTER: "https://twitter.com/theblitzapp",
  BLITZ_INSTAGRAM: "https://www.instagram.com/theblitzapp/",
  BLITZ_DISCORD: "https://blitz.gg/discord",
  BLITZ_DISCORD_VANITY: "https://discord.gg/theblitzapp",
  BLITZ_REDDIT: "https://www.reddit.com/r/BlitzApp/",
  BLITZ_TIKTOK: "https://www.tiktok.com/@theblitzapp",
  BLITZ_NAVER: "https://cafe.naver.com/blitzgg",
  BLITZ_MEDIUM: "https://medium.com/blitz-press",
  GREENHOUSE_DEPARTMENTS:
    "https://boards-api.greenhouse.io/v1/boards/blitzapp/departments",
  GREENHOUSE_JOBS: "https://boards-api.greenhouse.io/v1/boards/blitzapp/jobs",
  DOWNLOAD_INTER_FONT: "https://fonts.google.com/download?family=Inter",
  YOUTUBE: "https://www.youtube.com",
  YOUTUBE_THUMBNAIL: "https://img.youtube.com",
  YOUTUBE_EMBED: "https://www.youtube.com/embed",
  LEGAL_JAMSADAR: "https://www.jamsadr.com",
  LEGAL_OPTOUT: "https://liveramp.com/opt_out",
  LEGAL_BIS_DOC: "https://www.bis.doc.gov/index.php/exporter-portal",
  DOWNLOAD_LOL: "https://signup.leagueoflegends.com/en-us/signup/download",
  DOWNLOAD_TFT: "https://signup.leagueoflegends.com/en-us/signup/download",
  DOWNLOAD_FN: "https://store.epicgames.com/en-US/p/fortnite",
  DOWNLOAD_VALORANT: "https://playvalorant.com/en-us/download",
  DOWNLOAD_CSGO:
    "https://store.steampowered.com/app/730/CounterStrike_Global_Offensive/",
  DOWNLOAD_APEX: "https://store.steampowered.com/app/1172470/Apex_Legends/",
  DOWNLOAD_EFT: "https://www.escapefromtarkov.com/preorder-page",
  VALORANT_PREMIER_FAQ:
    "https://playvalorant.com/en-us/news/game-updates/premier-launch-faq/",
  TSM_FORTNITE_DISCORD: "https://discord.gg/D9kEas45ex",
  TSM_FORTNITE_DEVPAGE: "https://www.fortnite.com/@goswifty",
});

export const FALLBACK_IMAGE_URL = `${appURLs.CDN_PLAIN}/blitz/ui/img/fallback.svg`;

export const GRID_ICON_URL = `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/grid.svg`;

// This is giga-cringe and probably misplaced, it needs to be the app/games list
// !!! DO NOT ADD MORE HERE !!!
// Please don't add more here! use `app/games.mts`
export const GAME_SYMBOLS = [
  // Please don't add more here! use `app/games.mts`
  GAME_SYMBOL_LOL,
  GAME_SYMBOL_TFT,
  GAME_SYMBOL_UNKNOWN,
  GAME_SYMBOL_VAL,
  GAME_SYMBOL_CS2,
  GAME_SYMBOL_EFT,
  GAME_SYMBOL_APEX,
  GAME_SYMBOL_FORTNITE,
  // Please don't add more here! use `app/games.mts`
] as const;

export const GAME_ICONS = new SymbolMap<GameRecord<string>>({
  ...deriveGamesPropMap("icon"),
});

const INSTALL_REF_PARAM = "ref=blitz-app";

export const GAME_INSTALL_LINKS = new SymbolMap({
  ...deriveGamesPropMap("installLink"),

  // TODO: move these into GameDefinition
  // DO NOT ADD ANYTHING HERE
  [GAME_SYMBOL_LOL]: `${hardCodeURLs.DOWNLOAD_LOL}/?${INSTALL_REF_PARAM}`,
  [GAME_SYMBOL_TFT]: `${hardCodeURLs.DOWNLOAD_TFT}/?${INSTALL_REF_PARAM}`,
  [GAME_SYMBOL_VAL]: `${hardCodeURLs.DOWNLOAD_VALORANT}/?${INSTALL_REF_PARAM}`,
  [GAME_SYMBOL_FORTNITE]: `${hardCodeURLs.DOWNLOAD_FN}/?${INSTALL_REF_PARAM}`,
  [GAME_SYMBOL_CS2]: `${hardCodeURLs.DOWNLOAD_CSGO}/?${INSTALL_REF_PARAM}`,
  [GAME_SYMBOL_APEX]: `${hardCodeURLs.DOWNLOAD_APEX}/?${INSTALL_REF_PARAM}`,
  [GAME_SYMBOL_EFT]: `${hardCodeURLs.DOWNLOAD_EFT}/?${INSTALL_REF_PARAM}`,
});

export const GAME_ICON_SHAPES = new SymbolMap({
  ...deriveGamesPropMap("iconShape"),
});

export const GAME_SHORT_NAMES = new SymbolMap(deriveGamesPropMap("shortName"));

export const GAME_SYMBOL_BY_SHORT_NAME = {
  ...SymbolMap.invert(GAME_SHORT_NAMES),

  csgo: GAME_SYMBOL_CS2,
  // these are the names in core -> symbol
  LoL: GAME_SYMBOL_LOL,
  TFT: GAME_SYMBOL_TFT,
  Valorant: GAME_SYMBOL_VAL,
  FN: GAME_SYMBOL_FORTNITE,
  CSGO: GAME_SYMBOL_CS2,
  CS2: GAME_SYMBOL_CS2,
  Apex: GAME_SYMBOL_APEX,
  EFT: GAME_SYMBOL_EFT,
  Deadlock: GAME_SYMBOL_DEADLOCK,
  MarvelRivals: GAME_SYMBOL_MARVEL_RIVALS,
  Minecraft: GAME_SYMBOL_MINECRAFT,
} as const;

export const GAME_BACKEND_NAMES = new SymbolMap({
  ...deriveGamesPropMap("backendName"),
});

export const GAME_SYMBOL_FROM_BACKEND = SymbolMap.invert(GAME_BACKEND_NAMES);

export const GAME_SHORT_NAME_FROM_BACKEND = {};
for (const [k, v] of GAME_BACKEND_NAMES) {
  GAME_SHORT_NAME_FROM_BACKEND[v] = GAME_SHORT_NAMES[k];
}

export const GAME_BOX_ICONS = new SymbolMap<GameRecord<SVGComponent>>(
  deriveGamesPropMap("iconBox"),
);

export const GAME_IMG_HEROS = new SymbolMap<GameRecord<string>>(
  deriveGamesPropMap("imageHero"),
);

export const GAME_IMG_KEYARTS = new SymbolMap<GameRecord<string>>(
  deriveGamesPropMap("imageKeyart"),
);

export const GAME_IMG_BANNERS = new SymbolMap<GameRecord<string>>(
  deriveGamesPropMap("imageBanner"),
);

export const GAME_FRIEND_ACTIVITY_ICON_BOXES = new SymbolMap<
  GameRecord<SVGComponent>
>(deriveGamesPropMap("iconBadge"));

export const GAME_NAME_MAP = new SymbolMap<GameRecord<Translation>>(
  deriveGamesPropMap("fullName"),
);

export const GAME_FRIENDLY_SHORT_NAME_MAP = new SymbolMap<
  GameRecord<Translation>
>(deriveGamesPropMap("friendlyName"));

const GAME_PREFERS_SHORT_NAME = [
  ...gamesList
    .filter(
      (game): game is GamesWhereProp<"preferFriendlyName", true> =>
        game.preferFriendlyName,
    )
    .map(({ gameSymbol }) => gameSymbol),
];

export const GAME_PREFERRED_NAME_MAP = new SymbolMap({
  ...GAME_NAME_MAP,
  ...GAME_PREFERS_SHORT_NAME.reduce((acc, gameSymbol) => {
    acc[gameSymbol] = GAME_FRIENDLY_SHORT_NAME_MAP[gameSymbol];
    return acc;
  }, {}),
} as Record<GameSymbol, Translation>);

export const GAME_HAS_PLAYER_NAME_HASH = new SymbolMap<GameRecord<boolean>>(
  deriveGamesPropMap("playerNameHash"),
);

// Don't do this, this can't be assumed to be static
type _FilterGameSymbol<V> =
  typeof GAME_ACTIVE_MAP extends SymbolMap<infer T>
    ? { [K in keyof T]: V extends T[K] ? K : never }[keyof T]
    : never;

const keysProxy = (keyFn) =>
  new Proxy([], {
    get(_, prop) {
      const array = keyFn();

      const fn = Array.prototype[prop];
      if (typeof fn === "function") return fn.bind(array);

      return array[prop];
    },
    ownKeys(target) {
      return [...keyFn(), ...Reflect.ownKeys(target)];
    },
  });

// Use this as the single source of truth for a general list of games.
export const GAME_ACTIVE_MAP = new SymbolMap(deriveGamesPropMap("isActive"));

// Derived from the above.
export const ACTIVE_GAMES: GameSymbol[] = keysProxy(getActiveGameArray);

function getActiveGameArray() {
  return Array.from(GAME_ACTIVE_MAP)
    .filter((entry) => entry[1] === true)
    .map(([k]) => k);
}

export const GAMES_COMING_SOON = keysProxy(getComingSoonGamesArray);

function getComingSoonGamesArray() {
  return Array.from<[GameSymbol, boolean | "comingSoon"]>(GAME_ACTIVE_MAP)
    .filter(([_, value]) => typeof value === "string" && value === "comingSoon")
    .map(([k]) => k);
}

export const GAMES_TO_DISPLAY = Array.from<
  [GameSymbol, boolean | "comingSoon"]
>(GAME_ACTIVE_MAP)
  .filter(
    // This type annotation is necessary to ensure TypeScript doesn't infer
    // an overly strict type if we have no coming soon games.
    ([_, value]) => value === true || value === "comingSoon",
  )
  .map(([k]) => k);

// Lame manual list I was told to order specifically like this
// They dont want results in the same order as GAME_ACTIVE_MAP
export const GAMES_PLAYER_RELEVANCE = [
  GAME_SYMBOL_LOL,
  GAME_SYMBOL_TFT,
  GAME_SYMBOL_VAL,
  GAME_SYMBOL_CS2,
  GAME_SYMBOL_APEX,
  GAME_SYMBOL_FORTNITE,
  GAME_SYMBOL_EFT,
];

export type GameRecord<V> = PartialRecord<GameSymbol, V>;

export const GAME_COLORS = new SymbolMap<GameRecord<string>>(
  deriveGamesPropMap("gameColor"),
);

export const LOCALES: [string, string][] = [
  ["en", "English"],
  ["de", "Deutsch"],
  ["es", "Español"],
  ["fr", "Français"],
  ["it", "Italiano"],
  ["nl", "Nederlands"],
  ["pt", "Português"],
  ["pl", "Polski"],
  ["ru", "Русский"],
  ["tr", "Türkçe"],
  ["cs", "Čeština"],
  ["el", "Ελληνικά"],
  ["ko", "한국어"],
  ["ja", "日本語"],
  ["vi", "Tiếng Việt"],
  ["zh-Hans-CN", "简体中文"],
  ["zh-Hant-TW", "繁體中文"],
];

export const TOOLTIP_MAX_WIDTH = "380px";
export const TOOLTIP_OFFSET = "var(--sp-1_5)";

export const THEME_DARK = "DARK" as const;
export const THEME_BLUE = "BLUE" as const;
export const THEME_AUBERGINE = "AUBERGINE" as const;
export const THEME_GHERKIN = "GHERKIN" as const;
export const THEME_HALLOWEEN = "HALLOWEEN" as const;

export const NOTIFICATION_SEVERITY_LEVELS = ["critical", "major", "minor"];

export const HUB_SYMBOL_HOME: unique symbol = symbolName("home");
export const HUB_SYMBOL_COACHING: unique symbol = symbolName("coaching");
export const HUB_SYMBOL_OVERLAYS: unique symbol = symbolName("overlays");
export const HUB_SYMBOL_GAME_GUIDE: unique symbol = symbolName("game-guides");
export const HUB_SYMBOL_SETTINGS: unique symbol = symbolName("settings");

export const HUB_NAME_MAP = new SymbolMap({
  [HUB_SYMBOL_HOME]: "",
  [HUB_SYMBOL_OVERLAYS]: "overlays",
  [HUB_SYMBOL_GAME_GUIDE]: "game-guides",
  [HUB_SYMBOL_SETTINGS]: "settings",
});

export const HUB_PAGES = Object.getOwnPropertySymbols(HUB_NAME_MAP).map(
  (s: keyof typeof HUB_NAME_MAP) => HUB_NAME_MAP[s],
);

export const HUB_PAGE_PARAMS = HUB_PAGES.join("|");

//LEGAL INFO / POLICY INFO
export const LEGAL_PRIVACY_POLICY = "Privacy Policy";
export const LEGAL_TOS = "Terms of Service";

export const LEGAL_BLITZ_BATTLES_RULES = "Blitz Battles Rules";
const LEGAL_BASE_PATH = "/legal";
export const LEGAL_PRIVACY_POLICY_HREF = `${LEGAL_BASE_PATH}/privacy-policy`;
export const LEGAL_TOS_HREF = `${LEGAL_BASE_PATH}/terms-of-service`;
const LEGAL_BLITZ_BATTLES_HREF = `${LEGAL_BASE_PATH}/blitz-battles-rules`;

export const LEGAL_TABS = {
  [LEGAL_PRIVACY_POLICY]: {
    url: LEGAL_PRIVACY_POLICY_HREF,
    text: LEGAL_PRIVACY_POLICY,
    title: {
      key: "home:footer.pp",
      value: LEGAL_PRIVACY_POLICY,
    },
  },
  [LEGAL_TOS]: {
    url: LEGAL_TOS_HREF,
    text: LEGAL_TOS,
    title: {
      key: "home:footer.tos",
      value: LEGAL_TOS,
    },
  },
  [LEGAL_BLITZ_BATTLES_RULES]: {
    url: LEGAL_BLITZ_BATTLES_HREF,

    text: LEGAL_BLITZ_BATTLES_RULES,
    title: {
      key: "home:footer.battlesRules",
      value: LEGAL_BLITZ_BATTLES_RULES,
    },
  },
};

// CONTENT_HEADER_Z_INDEX
// must be higher than non-floating page content
// must be lower than floating content and navigation
export const CONTENT_HEADER_Z_INDEX = 5;

// Show the new dot on menu items for 3 weeks
export const NEW_FEATURE_DURATION = 1000 * 60 * 60 * 24 * 7 * 3;

export const APP_SCROLLER = "app-scroller"; // Class name

export const I18N_PLACEHOLDER = "[?]"; // Universal placeholder missing value

/** TODO: For Apex Legends platform, but we do not have this data yet! */
// import PlatformXbox from "@/inline-assets/platform-xbox.svg";
// import PlatformPlaystation from "@/inline-assets/platform-playstation.svg";
// import PlatformSwitch from "@/inline-assets/platform-switch.svg";
// import PlatformPc from "@/inline-assets/platform-switch.svg";

// export const GAME_PLATFORMS = {
//   xbox: {
//     label: ["common:platform.xbox", "Xbox"],
//     Icon: PlatformXbox,
//   },
//   ps3: {
//     label: ["common:platform.ps3", "PS3"],
//     Icon: PlatformPlaystation,
//   },
//   ps4: {
//     label: ["common:platform.ps4", "PS4"],
//     Icon: PlatformPlaystation,
//   },
//   ps5: {
//     label: ["common:platform.ps5", "PS5"],
//     Icon: PlatformPlaystation,
//   },
//   // Nintendo Switch
//   nx: {
//     label: ["common:platform.nx", "Switch"],
//     Icon: PlatformSwitch,
//   },
//   // TODO: Add steam icon ?
//   steam: {
//     label: ["common:platform.steam", "Steam"],
//     Icon: PlatformPc,
//   },
// } as const;

export const EVENT_CUSTOM_ERROR = "EVENT_CUSTOM_ERROR" as const;
export const EVENT_APP_DOWNLOAD = "EVENT_APP_DOWNLOAD" as const;
export const EVENT_UNINSTALL_SURVEY = "EVENT_UNINSTALL_SURVEY" as const;
export const EVENT_PROMO_MODAL_CLOSE = "EVENT_PROMO_MODAL_CLOSE" as const;
export const EVENT_HIGH_MEMORY_REFRESH = "EVENT_HIGH_MEMORY_REFRESH" as const;
export const EVENT_FEATURE_VERSION = "EVENT_FEATURE_VERSION" as const;
export const GAME_CLIP_SETTINGS = {
  VIDEO_QUALITY: {
    LOW: "low",
    MEDIUM: "medium",
    HIGH: "high",
  },
  BUFFER: {
    SHORT: "short",
    LONG: "long",
    MEDIUM: "medium",
  },
  EVENTS: {
    KILLS: "kills",
    ASSISTS: "assists",
    HEADSHOTS: "headshots", // This is a bit of a misnomer, it means "headshot kills"
  },
  FPS: {
    30: 30,
    60: 60,
    120: 120,
    240: 240,
  },
} as const;
